"use client";

import Link from "next/link";
import Image from "next/image";
import { COMPONENT_IMAGE_CONFIG, getImageProps, getOptimizedImageUrl } from '@/lib/imageConfig';
import React from "react";
import { extractArticleExcerpt } from "@/lib/seoUtils";
import { Clock, Eye } from "lucide-react";

interface ArticleCardProps {
  article: {
    id: string;
    title: string;
    slug: string;
    intro: string | null;
    content: string | null;
    header_image_url: string | null;
    view_count: number;
    reading_time: number;
    created_at: string;
    is_public?: boolean;
  };
}

export const ArticleCard: React.FC<ArticleCardProps> = React.memo(({ article }) => {
  // Use intro if available, otherwise fallback to excerpt from content
  const displayText = article.intro || extractArticleExcerpt(article.content || '', 120);
  // Use database reading_time field instead of calculating
  const readingTime = `${article.reading_time} דק׳`;

  // For drafts, link to draft preview page; for public articles, use slug
  const articleUrl = article.is_public === false
    ? `/articles/draft/${article.id}`
    : `/articles/${article.slug}`;

  return (
    <Link
      href={articleUrl}
      className="group bg-white border border-orange-200 rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-orange-100/50 transition-all duration-300 flex flex-col h-full hover:border-orange-300"
    >
      {/* Header Image */}
      {article.header_image_url && (
        <div className="relative w-full h-48 bg-gradient-to-br from-orange-100 to-amber-100 overflow-hidden">
          <Image
            src={getOptimizedImageUrl(article.header_image_url, COMPONENT_IMAGE_CONFIG.ARTICLE_CARD.size, COMPONENT_IMAGE_CONFIG.ARTICLE_CARD.quality)}
            alt={article.title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            {...getImageProps(COMPONENT_IMAGE_CONFIG.ARTICLE_CARD)}
          />
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      )}

      {/* Content */}
      <div className="p-6 text-right flex-1 flex flex-col" dir="rtl" lang="he">
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors line-clamp-2 leading-tight">
          {article.title}
        </h3>

        {displayText && (
          <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed flex-1 text-base">
            {displayText}
          </p>
        )}


        {/* Metadata */}
        <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-orange-100">
          <div className="flex items-center gap-2 text-orange-600">
            <Clock className="h-4 w-4" />
            <span className="font-medium">זמן קריאה: {readingTime}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <Eye className="h-4 w-4" />
            <span className="font-medium">{article.view_count} צפיות</span>
          </div>
        </div>
      </div>
    </Link>
  );
});
